import { useLocation } from 'react-router';
import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import { PremiumWidgetDenied } from '@/features/dashboard';
import ChartsBlock from '@/features/markets/components/Chart';
import CommoditiesTable from '@/features/markets/components/CommoditiesTable';
import Index from '@/features/markets/components/CustomChart';
import { Feed } from '@/features/markets/components/Feed';
import type { FeedFilters } from '@/features/markets/components/Feed/api/fetchFeedData';
import MiniTablesContainer from '@/features/markets/components/MiniTablesContainer';
import MultiGroupTable from '@/features/markets/components/MultiGroupTable';
import SpotHistoryTable from '@/features/markets/components/SpotHistoryTable';
import type { BaseTabType, MarketsStaticDataChartType } from '@/features/markets/types';
import { StaticDataChart } from '@/features/markets/widgets/StaticDataChart';
import CsvTableWidget from '../CsvTableWidget';
import FilesWidget from '../FilesWidget';

type NumericLayout = { fullWidth?: boolean; height?: number };

function normalizeLayout(layout: BaseTabType['layout']): NumericLayout | undefined {
  if (!layout) return undefined;

  const height = Number(layout.height);
  const fullWidth =
    typeof layout.fullWidth === 'string' ? layout.fullWidth === 'true' : layout.fullWidth;

  return {
    ...(typeof fullWidth === 'boolean' ? { fullWidth } : {}),
    ...(Number.isFinite(height) && height > 0 ? { height } : {})
  };
}

const isStaticDataChartTab = (tab: BaseTabType): tab is MarketsStaticDataChartType =>
  tab.type === 'graph' && tab.metadata?.graphType === 'static';

export const MarketWidget = ({ tab }: { tab: BaseTabType }) => {
  const { hasPermission } = usePermission();
  const location = useLocation();
  const layout = normalizeLayout(tab.layout);

  const isPremiumWidgetPermissionGranted =
    hasPermission(Permission.DashboardPremiumWidgets) || !location.pathname.includes('dashboard');

  switch (tab.type) {
    case 'table':
      return (
        <CommoditiesTable
          key={tab.id}
          layout={layout}
          sections={tab.sections}
        />
      );
    case 'multi_group_table':
      return (
        <MultiGroupTable
          key={tab.id}
          layout={layout}
          sections={tab.sections}
        />
      );
    case 'graph':
      if (!isPremiumWidgetPermissionGranted) {
        return <PremiumWidgetDenied />;
      }
      if (tab.metadata?.graphType === 'custom') {
        return (
          <Index
            key={tab.id}
            layout={layout}
            metadata={tab.metadata}
          />
        );
      }
      if (isStaticDataChartTab(tab)) {
        return (
          <StaticDataChart
            data={{
              tool_call_id: tab.tool_call_id ?? tab.id,
              title: tab.name,
              chartConfig: tab.chartConfig
            }}
          />
        );
      }
      return (
        <ChartsBlock
          key={tab.id}
          layout={layout}
          tab={tab}
        />
      );

    case 'feed':
      return (
        <Feed
          key={tab.id}
          metadata={tab.metadata as FeedFilters}
        />
      );

    case 'minitable':
      return <MiniTablesContainer tables={tab.tables} />;
    case 'spot_history_table':
      return (
        <SpotHistoryTable
          key={tab.id}
          columns={tab.columns}
          layout={layout}
          rows={tab.rows}
        />
      );
    case 'csvtable':
      return (
        <CsvTableWidget
          fileName={tab.fileName}
          groupName={tab.groupName}
          layout={layout}
          metadata={tab.metadata}
        />
      );
    case 'file_list':
      return (
        <FilesWidget
          downloadable={tab.metadata?.downloadable}
          groupName={tab.groupName}
          size={tab.size}
        />
      );
    default:
      return null;
  }
};
